import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { safeCopy } from "../lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fgm_ambassador_token";

/* ============ STAR / RANK VISUAL ============ */
function StarRow({ filled, total = 5, size = "lg" }) {
    const dim = size === "lg" ? "text-3xl" : size === "md" ? "text-xl" : "text-sm";
    return (
        <span className={`font-mono ${dim} tracking-tight`}>
            {Array.from({ length: total }, (_, i) => (
                <span key={i} className={i < filled ? "text-[#E63946]" : "text-zinc-700"}>★</span>
            ))}
        </span>
    );
}

function ProgressBar({ pct }) {
    return (
        <div className="h-1.5 w-full bg-zinc-800 overflow-hidden">
            <div
                className="h-full bg-gradient-to-r from-[#E63946] to-[#ff6b78] transition-all duration-700 ease-out"
                style={{ width: `${pct}%` }}
            />
        </div>
    );
}

function ReqRow({ req }) {
    const isStarRow = req.key === "prev_rank";
    return (
        <div
            data-testid={`req-row-${req.key}`}
            className={`flex items-center justify-between gap-2 py-2 border-b border-white/5 ${req.done ? "opacity-100" : "opacity-90"}`}
        >
            <div className="flex items-center gap-2 min-w-0">
                <span className={`text-base ${req.done ? "text-[#E63946]" : "text-zinc-700"}`}>
                    {req.done ? "●" : "○"}
                </span>
                <span className="text-sm text-zinc-300 truncate">{req.label}</span>
            </div>
            <span className="font-mono text-xs text-zinc-400 whitespace-nowrap">
                {isStarRow
                    ? (req.done ? "✓" : "—")
                    : `${req.current}/${req.target}`}
            </span>
        </div>
    );
}

/* ============ LOGIN SCREEN ============ */
function LoginCard({ onLogin }) {
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await axios.post(`${API}/ambassador/login`, {
                email: email.trim().toLowerCase(),
                referral_code: code.trim(),
            });
            localStorage.setItem(TOKEN_KEY, res.data.token);
            onLogin(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-fgm-bg flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-fgm-surface border border-white/10 p-8" style={{ background: "linear-gradient(180deg, #141414 0%, #0a0a0a 100%)" }}>
                <div className="text-center mb-6">
                    <div className="text-[10px] font-display tracking-[0.4em] uppercase text-[#E63946] mb-2">⚡ FGM Apparel</div>
                    <h1 className="font-display text-3xl tracking-tight text-white uppercase leading-tight">Ambassador Portal</h1>
                    <p className="text-sm text-zinc-400 mt-2">Sign in with the email + referral code from your acceptance message.</p>
                </div>
                <form onSubmit={submit} className="space-y-3">
                    <input
                        data-testid="amb-login-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        autoComplete="email"
                        className="w-full bg-black border border-white/20 px-4 py-3 text-white text-sm focus:border-[#E63946] focus:outline-none"
                    />
                    <input
                        data-testid="amb-login-code"
                        type="text"
                        required
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="your-referral-code"
                        className="w-full bg-black border border-white/20 px-4 py-3 text-white text-sm font-mono focus:border-[#E63946] focus:outline-none"
                    />
                    <button
                        data-testid="amb-login-submit"
                        type="submit"
                        disabled={busy}
                        className="w-full bg-[#E63946] text-white font-display tracking-[0.3em] uppercase text-sm py-3 hover:bg-white hover:text-[#E63946] transition-colors disabled:opacity-50"
                    >
                        {busy ? "Signing in…" : "Enter Portal →"}
                    </button>
                </form>
                <p className="text-[10px] text-zinc-600 mt-4 text-center">
                    Not an ambassador yet? <a href="#ambassador" className="text-[#E63946] hover:underline">Apply here</a>.
                </p>
            </div>
        </div>
    );
}

/* ============ EARNINGS / RANK CAROUSEL ============ */
function EarningsAndRank({ data, onRefresh }) {
    const [tierIdx, setTierIdx] = useState(0);
    const ladder = data.ladder || [];
    const current = ladder[tierIdx];
    const next = ladder.find((t) => !t.achieved) || ladder[ladder.length - 1];

    useEffect(() => {
        // On load, jump to the first not-yet-achieved tier (their goal)
        const firstUndone = ladder.findIndex((t) => !t.achieved);
        if (firstUndone >= 0) setTierIdx(firstUndone);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return (
        <div className="space-y-3">
            {/* Earnings */}
            <div className="bg-fgm-surface border border-white/10 p-5">
                <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500 mb-2">Earnings — Lifetime</div>
                <div className="flex items-baseline gap-3">
                    <div className="text-4xl sm:text-5xl font-display tracking-tight text-white font-bold">
                        ${data.stats?.commission_earned?.toFixed(2) ?? "0.00"}
                    </div>
                    <div className="text-xs font-display tracking-[0.25em] uppercase text-[#E63946] font-bold">
                        {(data.stats?.commission_pct ?? 0) > 0
                            ? `${data.stats.commission_pct}% rate`
                            : "Onboarding"}
                    </div>
                </div>
                <div className="text-xs text-zinc-500 mt-1 font-mono">
                    ${data.stats?.gross_sales?.toFixed(2) ?? "0.00"} gross · {data.stats?.orders ?? 0} qualifying orders
                </div>
                {data.next_unlock && (
                    <div className="mt-3 pt-3 border-t border-white/5 text-xs text-zinc-400">
                        <span className="text-[#E63946] font-bold">↑</span> Climb to{" "}
                        <span className="text-white font-display tracking-wider">{data.next_unlock.label}</span>{" "}
                        to {(data.stats?.commission_pct ?? 0) > 0 ? "earn" : "start earning"}{" "}
                        <span className="text-white font-bold">{data.next_unlock.rate}%</span> per sale
                    </div>
                )}
            </div>

            {/* Rank Card */}
            <div className="bg-fgm-surface border border-[#E63946]/30 p-5" style={{ background: "linear-gradient(180deg, #1a0a0d 0%, #0a0a0a 100%)" }}>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500">Your Current Rank</div>
                    <button
                        data-testid="rank-refresh"
                        onClick={() => onRefresh()}
                        className="text-zinc-500 hover:text-white text-xs"
                        title="Refresh stats"
                    >
                        ↻
                    </button>
                </div>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                    <div>
                        <div className="font-display text-2xl tracking-tight text-white uppercase">{data.current_label}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                            {data.current_rank >= 5
                                ? "MAX RANK — you're a legend"
                                : `Goal: ${next?.label || "—"}`}
                        </div>
                    </div>
                    <StarRow filled={data.current_rank} />
                </div>

                {/* Tier carousel */}
                <div className="flex items-center gap-2 mb-3 border-t border-white/5 pt-3">
                    <button
                        data-testid="rank-prev"
                        onClick={() => setTierIdx((i) => Math.max(0, i - 1))}
                        disabled={tierIdx === 0}
                        className="text-zinc-400 hover:text-white disabled:opacity-30 text-lg px-2"
                        aria-label="Previous tier"
                    >‹</button>
                    <div className="flex-1 text-center">
                        <div className="font-display text-sm tracking-[0.25em] uppercase text-white">
                            How to become a {current?.label || "—"}
                        </div>
                        <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500 mt-0.5">
                            Progress {current?.percent ?? 0}%
                        </div>
                    </div>
                    <button
                        data-testid="rank-next"
                        onClick={() => setTierIdx((i) => Math.min(ladder.length - 1, i + 1))}
                        disabled={tierIdx === ladder.length - 1}
                        className="text-zinc-400 hover:text-white disabled:opacity-30 text-lg px-2"
                        aria-label="Next tier"
                    >›</button>
                </div>

                <ProgressBar pct={current?.percent ?? 0} />

                <div className="mt-4">
                    {(current?.requirements || []).map((r) => (
                        <ReqRow key={r.key} req={r} />
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ============ PAYOUT INFO (self-serve) ============ */
const PAYOUT_METHODS = [
    { value: "", label: "— select —" },
    { value: "venmo", label: "Venmo" },
    { value: "paypal", label: "PayPal" },
    { value: "zelle", label: "Zelle" },
    { value: "cashapp", label: "Cash App" },
    { value: "store_credit", label: "FGM store credit only" },
    { value: "other", label: "Other (we'll DM you)" },
];

function PayoutInfoPanel({ token, ambassador, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const [method, setMethod] = useState(ambassador.payout_method || "");
    const [handle, setHandle] = useState(ambassador.payout_handle || "");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setMethod(ambassador.payout_method || "");
        setHandle(ambassador.payout_handle || "");
    }, [ambassador.payout_method, ambassador.payout_handle]);

    const placeholderFor = (m) => ({
        venmo: "@your-venmo",
        paypal: "you@email.com",
        zelle: "email or phone",
        cashapp: "$cashtag",
        store_credit: "(leave blank)",
        other: "we'll follow up",
    }[m] || "@username, email, or phone");

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await axios.patch(`${API}/ambassador/me/payout`,
                { payout_method: method || null, payout_handle: handle || null },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Payout info saved");
            setEditing(false);
            onUpdated && onUpdated();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const hasInfo = ambassador.payout_method && (ambassador.payout_method === "store_credit" || ambassador.payout_handle);

    return (
        <div data-testid="payout-info-panel" className="bg-fgm-surface border border-white/10 p-5 space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500">
                    💰 Where We Send Your Commission
                </div>
                {!editing && (
                    <button
                        data-testid="payout-info-edit"
                        onClick={() => setEditing(true)}
                        className="text-[10px] font-display tracking-[0.25em] uppercase text-[#E63946] hover:text-white"
                    >
                        {hasInfo ? "Edit" : "+ Add"}
                    </button>
                )}
            </div>

            {!editing && (
                hasInfo ? (
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="font-display text-sm tracking-[0.25em] uppercase text-white">
                                {ambassador.payout_method}
                            </div>
                            {ambassador.payout_handle && (
                                <div className="font-mono text-xs text-zinc-400 mt-0.5">{ambassador.payout_handle}</div>
                            )}
                        </div>
                        <div className="text-[9px] font-display tracking-[0.3em] uppercase text-green-400">● Ready</div>
                    </div>
                ) : (
                    <div className="text-xs text-yellow-400/80 italic">
                        ⚠ Add your payout info so we can pay you 10% commission at month-end.
                    </div>
                )
            )}

            {editing && (
                <form onSubmit={save} className="space-y-2">
                    <select
                        data-testid="payout-info-method"
                        value={method}
                        onChange={(e) => setMethod(e.target.value)}
                        className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white"
                    >
                        {PAYOUT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <input
                        data-testid="payout-info-handle"
                        value={handle}
                        onChange={(e) => setHandle(e.target.value)}
                        placeholder={placeholderFor(method)}
                        className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white font-mono"
                    />
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="payout-info-save"
                            type="submit"
                            disabled={busy}
                            className="flex-1 bg-[#E63946] text-white font-display tracking-[0.25em] uppercase text-[10px] py-2 hover:bg-white hover:text-[#E63946] disabled:opacity-50"
                        >
                            {busy ? "Saving…" : "Save"}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setEditing(false); setMethod(ambassador.payout_method || ""); setHandle(ambassador.payout_handle || ""); }}
                            className="text-[10px] font-display tracking-[0.25em] uppercase px-4 py-2 border border-white/15 text-zinc-400 hover:text-white"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}


/* ============ CODES + SHARE LINKS ============ */
function CodesPanel({ data }) {
    const refCode = data.ambassador?.referral_code || "";
    const discCode = data.ambassador?.discount_code;
    const site = data.site_url || "https://fgmapparel.com";
    const shareUrl = refCode ? `${site}/?ref=${refCode}` : site;

    const copy = async (val, label) => {
        const ok = await safeCopy(val);
        toast[ok ? "success" : "error"](ok ? `${label} copied` : "Copy blocked");
    };

    return (
        <div className="bg-fgm-surface border border-white/10 p-5 space-y-4">
            <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500">
                Your Codes & Share Link
            </div>

            <div>
                <div className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 mb-1">Referral Code (your tracked link)</div>
                <div className="flex items-center gap-2">
                    <div className="flex-1 bg-black border border-white/10 px-3 py-2 font-mono text-sm text-white truncate" data-testid="code-referral">
                        {refCode || "—"}
                    </div>
                    <button onClick={() => copy(refCode, "Referral code")} className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-2 border border-white/20 text-white hover:bg-white hover:text-black">
                        Copy
                    </button>
                </div>
                <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1 bg-black border border-white/10 px-3 py-2 font-mono text-xs text-zinc-400 truncate" data-testid="code-share-url">
                        {shareUrl}
                    </div>
                    <button onClick={() => copy(shareUrl, "Share link")} className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-2 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white">
                        Copy Link
                    </button>
                </div>
            </div>

            {discCode ? (
                <div>
                    <div className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 mb-1">10% Off Discount Code (for your followers)</div>
                    <div className="flex items-center gap-2">
                        <div className="flex-1 bg-black border-2 border-dashed border-[#E63946] px-3 py-2 font-mono text-sm text-white" data-testid="code-discount">
                            {discCode}
                        </div>
                        <button onClick={() => copy(discCode, "Discount code")} className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-2 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white">
                            Copy
                        </button>
                    </div>
                </div>
            ) : (
                <div className="text-xs text-zinc-500 italic">
                    Your personal 10% off discount code will appear here once it's been minted by admin.
                </div>
            )}
        </div>
    );
}

/* ============ UGC SUBMISSION ============ */
function UgcPanel({ token, onSubmitted }) {
    const [mode, setMode] = useState("link"); // 'link' or 'upload'
    const [url, setUrl] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [mine, setMine] = useState([]);
    const fileRef = useRef(null);

    const refresh = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ambassador/ugc`, { headers: { Authorization: `Bearer ${token}` } });
            setMine(res.data || []);
        } catch {
            /* ignore */
        }
    }, [token]);

    useEffect(() => { refresh(); }, [refresh]);

    const submitLink = async (e) => {
        e.preventDefault();
        if (!url.trim()) return;
        setBusy(true);
        try {
            await axios.post(`${API}/ambassador/ugc/link`,
                { url: url.trim(), note: note.trim() || null },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Link submitted · awaiting review");
            setUrl(""); setNote("");
            refresh();
            onSubmitted && onSubmitted();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submit failed");
        } finally {
            setBusy(false);
        }
    };

    const uploadFile = async (file) => {
        if (!file) return;
        if (file.size > 50 * 1024 * 1024) {
            toast.error("File must be ≤ 50 MB");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        if (note.trim()) fd.append("note", note.trim());
        setBusy(true);
        try {
            await axios.post(`${API}/ambassador/ugc/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`Uploaded ${file.name} · awaiting review`);
            setNote("");
            refresh();
            onSubmitted && onSubmitted();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-fgm-surface border border-white/10 p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500">Submit UGC for Ads</div>
                <div className="flex items-center gap-1">
                    {["link", "upload"].map((m) => (
                        <button
                            key={m}
                            data-testid={`ugc-mode-${m}`}
                            onClick={() => setMode(m)}
                            className={`text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border ${
                                mode === m ? "bg-[#E63946] border-[#E63946] text-white" : "border-white/15 text-zinc-400 hover:text-white"
                            }`}
                        >
                            {m === "link" ? "Paste Link" : "Upload File"}
                        </button>
                    ))}
                </div>
            </div>

            {mode === "link" ? (
                <form onSubmit={submitLink} className="space-y-2">
                    <input
                        data-testid="ugc-link-url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://instagram.com/p/... or TikTok URL"
                        required
                        className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white"
                    />
                    <textarea
                        data-testid="ugc-link-note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Optional note (what's the content about?)"
                        rows={2}
                        className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white"
                    />
                    <button
                        data-testid="ugc-link-submit"
                        type="submit"
                        disabled={busy}
                        className="bg-[#E63946] text-white font-display tracking-[0.25em] uppercase text-[10px] px-4 py-2 hover:bg-white hover:text-[#E63946] disabled:opacity-50"
                    >
                        {busy ? "Submitting…" : "+ Submit Link"}
                    </button>
                </form>
            ) : (
                <div className="space-y-2">
                    <textarea
                        data-testid="ugc-upload-note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Optional note (what's the content about?)"
                        rows={2}
                        className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white"
                    />
                    <input
                        ref={fileRef}
                        data-testid="ugc-upload-file"
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                        className="hidden"
                        onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }}
                    />
                    <button
                        type="button"
                        data-testid="ugc-upload-trigger"
                        onClick={() => fileRef.current?.click()}
                        disabled={busy}
                        className="bg-[#E63946] text-white font-display tracking-[0.25em] uppercase text-[10px] px-4 py-2 hover:bg-white hover:text-[#E63946] disabled:opacity-50"
                    >
                        {busy ? "Uploading…" : "↑ Choose File (≤50MB)"}
                    </button>
                </div>
            )}

            {mine.length > 0 && (
                <div className="border-t border-white/5 pt-4 space-y-2">
                    <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500">Your Submissions ({mine.length})</div>
                    {mine.slice(0, 10).map((u) => (
                        <div key={u.id} className="flex items-center justify-between gap-2 text-xs">
                            <div className="truncate flex-1">
                                <span className="text-zinc-400">{u.kind === "link" ? "🔗" : "📎"}</span>{" "}
                                <span className="text-zinc-300 font-mono truncate">{u.url || u.filename}</span>
                            </div>
                            <span className={`text-[9px] font-display tracking-[0.25em] uppercase px-2 py-0.5 border whitespace-nowrap ${
                                u.status === "approved" ? "border-green-500/40 text-green-400" :
                                u.status === "rejected" ? "border-zinc-500/40 text-zinc-500" :
                                "border-yellow-500/40 text-yellow-400"
                            }`}>
                                ● {u.status}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ============ MAIN PORTAL ============ */
export default function AmbassadorPortal() {
    const [token, setToken] = useState(() => {
        try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
    });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showApply, setShowApply] = useState(false);

    const load = useCallback(async (tkn) => {
        // Guard against accidental SyntheticEvent passing (e.g. onClick={load})
        const t = (typeof tkn === "string" && tkn) ? tkn : token;
        if (!t) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API}/ambassador/me`, { headers: { Authorization: `Bearer ${t}` } });
            setData(res.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                setToken(null);
                toast.error("Session expired — please sign in again");
            } else {
                toast.error(err?.response?.data?.detail || "Failed to load portal");
            }
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { if (token) load(); }, [token, load]);

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setData(null);
    };

    if (!token) {
        return <LoginCard onLogin={(res) => { setToken(res.token); load(res.token); }} />;
    }

    if (loading || !data) {
        return (
            <div className="min-h-screen bg-fgm-bg flex items-center justify-center">
                <div className="text-zinc-500 font-display tracking-[0.4em] uppercase text-xs">Loading…</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-fgm-bg" data-testid="ambassador-portal">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                    <div>
                        <div className="text-[10px] font-display tracking-[0.4em] uppercase text-[#E63946]">⚡ FGM Ambassador</div>
                        <h1 className="font-display text-2xl tracking-tight text-white uppercase mt-1">
                            Hey, {data.ambassador?.name?.split(" ")[0] || "Athlete"}
                        </h1>
                    </div>
                    <button
                        data-testid="amb-logout"
                        onClick={logout}
                        className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-2 border border-white/15 text-zinc-400 hover:text-white"
                    >
                        Sign Out
                    </button>
                </div>

                {/* Grid: rank+earnings (left col), codes+ugc (right col) */}
                <div className="grid lg:grid-cols-2 gap-5">
                    <EarningsAndRank data={data} onRefresh={load} />
                    <div className="space-y-5">
                        <CodesPanel data={data} />
                        <PayoutInfoPanel token={token} ambassador={data.ambassador} onUpdated={load} />
                        <UgcPanel token={token} onSubmitted={load} />
                    </div>
                </div>
            </div>
        </div>
    );
}
